import React from 'react';
import { AlertTriangle, Clock, DollarSign, TrendingUp } from 'lucide-react';

export const TheProblem = () => {
  const stats = [
    {
      number: '82%',
      label: 'of mobile apps failed security testing',
      source: 'NowSecure, 2024',
    },
    {
      number: '286',
      label: 'days before you even know you were breached',
      source: 'IBM Security',
    },
    {
      number: '$4.45M',
      label: 'the average bill when it happens',
      source: 'IBM Cost of Data Breach',
    },
  ];

  const problems = [
    {
      icon: Clock,
      title: 'You Ship Weekly. You Test Annually.',
      description: 'That means 51 weeks of unprotected releases. Every single year. Attackers know this.',
      severity: 'CRITICAL',
    },
    {
      icon: AlertTriangle,
      title: 'Your Scanner Is Guessing.',
      description: 'It checks patterns. It runs signatures. It has no idea what your app actually does. Business logic flaws? Invisible.',
      severity: 'CRITICAL',
    },
    {
      icon: DollarSign,
      title: '$50K Per Pentest. 3 Weeks Wait. A PDF.',
      description: 'That PDF is outdated before the ink dries. You shipped twice since then. The vulnerabilities shipped with you.',
      severity: 'HIGH',
    },
    {
      icon: TrendingUp,
      title: 'Your Attack Surface Doubles Every Year.',
      description: 'More APIs. More third-party SDKs. More user roles. More ways in. Your security team is the same size.',
      severity: 'HIGH',
    },
  ];

  return (
    <section className="relative overflow-hidden">
      <div className="section-divider"></div>

      <div className="container mx-auto px-6 py-28">
        <div className="max-w-5xl mx-auto">
          {/* Section header */}
          <div className="mb-20">
            <div className="tag mb-8">
              <span className="w-[6px] h-[6px] rounded-full bg-[#ff3333] shadow-[0_0_8px_#ff3333]"></span>
              The Uncomfortable Truth
            </div>
            <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
              Right Now, Someone
              <br />
              <span className="text-white/30">Is Testing Your App.</span>
            </h2>
            <p className="text-xl text-white/35 max-w-2xl" style={{ fontFamily: 'var(--font-body)' }}>
              The only question is whether it's you or an attacker.
            </p>
          </div>

          {/* Stats row */}
          <div className="grid md:grid-cols-3 gap-px mb-20 bg-white/[0.04]">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-[#020204] p-8 text-center group hover:bg-[#0d0d12] transition-colors"
              >
                <div className="font-mono text-5xl md:text-6xl font-bold text-[#00ffd5] mb-3 tracking-tight">
                  {stat.number}
                </div>
                <p className="text-white/40 text-sm mb-3" style={{ fontFamily: 'var(--font-body)' }}>
                  {stat.label}
                </p>
                <p className="font-mono text-[0.6rem] text-white/30 tracking-wider uppercase">
                  {stat.source}
                </p>
              </div>
            ))}
          </div>

          {/* Problem cards */}
          <div className="grid md:grid-cols-2 gap-4">
            {problems.map((problem, index) => (
              <div
                key={index}
                className="card-tactical corner-brackets rounded-sm p-7 group"
              >
                <div className="flex items-start gap-5">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#ff3333]/[0.06] border border-[#ff3333]/10">
                    <problem.icon className="w-5 h-5 text-[#ff3333]/70" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-white">
                        {problem.title}
                      </h3>
                      <span className={`font-mono text-[0.55rem] tracking-wider px-2 py-0.5 ${
                        problem.severity === 'CRITICAL'
                          ? 'text-[#ff3333] bg-[#ff3333]/[0.08] border border-[#ff3333]/20'
                          : 'text-[#ffb800] bg-[#ffb800]/[0.06] border border-[#ffb800]/15'
                      }`}>
                        {problem.severity}
                      </span>
                    </div>
                    <p className="text-white/35 text-sm leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
                      {problem.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TheProblem;
